import React, { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Input,
  Button,
  Table,
  Tabs,
  Space,
  Badge,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface UserData {
  key: string;
  name: string;
  email: string;
  role: string;
  dateAdded: string;
  dateCreated: string;
  status: "active" | "inactive";
}

const Users: React.FC = () => {
  const [activeTab, setActiveTab] = useState("active");
  const [searchValue, setSearchValue] = useState("");

  const userData: UserData[] = [
    {
      key: "1",
      name: "Eduardo Tolentino",
      email: "eduardo.tolentino@bicycleemporium.com",
      role: "Shop Employee",
      dateAdded: "02/14/2025",
      dateCreated: "02/14/2025",
      status: "active",
    },
    {
      key: "2",
      name: "Justin Galioto",
      email: "justin.galioto@bicycleemporium.com",
      role: "Shop Employee",
      dateAdded: "02/14/2025",
      dateCreated: "02/14/2025",
      status: "active",
    },
    {
      key: "3",
      name: "Samantha Fryer",
      email: "samantha.fryer@bicycleemporium.com",
      role: "Shop Employee",
      dateAdded: "02/01/2025",
      dateCreated: "02/01/2025",
      status: "active",
    },
    {
      key: "4",
      name: "Andrew Sutton",
      email: "andrew.sutton@bicycleemporium.com",
      role: "Shop Administrator",
      dateAdded: "02/01/2025",
      dateCreated: "02/01/2025",
      status: "active",
    },
    {
      key: "5",
      name: "Jermaine Wagner",
      email: "jermaine.wagner@bicycleemporium.com",
      role: "Shop Employee",
      dateAdded: "02/01/2025",
      dateCreated: "02/01/2025",
      status: "active",
    },
    {
      key: "6",
      name: "Terra Smith",
      email: "terra.smith@bicycleemporium.com",
      role: "Shop Administrator",
      dateAdded: "02/01/2025",
      dateCreated: "02/01/2025",
      status: "active",
    },
    {
      key: "7",
      name: "Tyler Ross",
      email: "tyler.ross@bicycleemporium.com",
      role: "Shop Employee",
      dateAdded: "01/27/2025",
      dateCreated: "01/27/2025",
      status: "active",
    },
  ];

  const getFilteredData = () => {
    let filteredData = userData;

    if (activeTab === "active") {
      filteredData = userData.filter((user) => user.status === "active");
    } else if (activeTab === "inactive") {
      filteredData = userData.filter((user) => user.status === "inactive");
    }

    if (searchValue) {
      filteredData = filteredData.filter(
        (user) =>
          user.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.email.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.role.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }

    return filteredData;
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 248,
      render: (text: string) => (
        <Text
          style={{
            color: "#4D4D4D",
            fontFamily: "Open Sans",
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "20px",
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 360,
      render: (text: string) => (
        <Text
          style={{
            color: "#4D4D4D",
            fontFamily: "Open Sans",
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "20px",
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 257,
      render: (text: string) => (
        <Text
          style={{
            color: "#4D4D4D",
            fontFamily: "Open Sans",
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "20px",
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: "Date Added",
      dataIndex: "dateAdded",
      key: "dateAdded",
      width: 259,
      render: (text: string) => (
        <Text
          style={{
            color: "#4D4D4D",
            fontFamily: "Open Sans",
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "20px",
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "dateCreated",
      key: "dateCreated",
      width: 259,
      render: (text: string) => (
        <Text
          style={{
            color: "#4D4D4D",
            fontFamily: "Open Sans",
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "20px",
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 141,
      render: () => (
        <Text
          style={{
            color: "#AAA",
            fontFamily: "Open Sans",
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "20px",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Edit
        </Text>
      ),
    },
  ];

  const filteredData = getFilteredData();
  const activeUsersCount = userData.filter(
    (user) => user.status === "active",
  ).length;

  const tabItems = [
    {
      key: "active",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src="/images/verified-icon.svg"
            alt="Active"
            style={{ width: "18px", height: "18px" }}
          />
          <span
            style={{
              color: activeTab === "active" ? "#007AFF" : "#AAA",
              fontFamily: "Open Sans",
              fontSize: "16px",
              fontWeight: activeTab === "active" ? "700" : "400",
              lineHeight: "16px",
              letterSpacing: "0.5px",
            }}
          >
            Active Users
          </span>
        </div>
      ),
      children: null,
    },
    {
      key: "inactive",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src="/images/minus-circle-icon.svg"
            alt="Inactive"
            style={{ width: "18px", height: "18px" }}
          />
          <span
            style={{
              color: activeTab === "inactive" ? "#007AFF" : "#AAA",
              fontFamily: "Open Sans",
              fontSize: "16px",
              fontWeight: activeTab === "inactive" ? "700" : "400",
              lineHeight: "16px",
              letterSpacing: "0.5px",
            }}
          >
            Inactive
          </span>
        </div>
      ),
      children: null,
    },
    {
      key: "all",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src="/images/infinity-icon.svg"
            alt="All"
            style={{ width: "18px", height: "18px" }}
          />
          <span
            style={{
              color: activeTab === "all" ? "#007AFF" : "#AAA",
              fontFamily: "Open Sans",
              fontSize: "16px",
              fontWeight: activeTab === "all" ? "700" : "400",
              lineHeight: "16px",
              letterSpacing: "0.5px",
            }}
          >
            All
          </span>
        </div>
      ),
      children: null,
    },
  ];

  return (
    <div
      style={{
        backgroundColor: "#FAFAFA",
        minHeight: "calc(100vh - 80px)",
        padding: "39px 24px 0",
      }}
    >
      <Row gutter={[0, 46]}>
        <Col span={24}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <img
              src="/images/users-icon.png"
              alt="Users"
              style={{ width: "36px", height: "36px" }}
            />
            <Title
              level={3}
              style={{
                margin: 0,
                color: "#4D4D4D",
                fontFamily: "Open Sans",
                fontSize: "24px",
                fontWeight: "700",
                lineHeight: "24px",
                letterSpacing: "0.5px",
              }}
            >
              Users
            </Title>
          </div>
        </Col>
      </Row>

      <Row gutter={[0, 30]}>
        <Col span={24}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{
              width: "100%",
            }}
            tabBarStyle={{
              borderBottom: "1px solid #CCC",
              marginBottom: "0",
            }}
            tabBarGutter={30}
          />
        </Col>
      </Row>

      <Row gutter={[0, 20]} style={{ marginTop: "30px" }}>
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <Input
                placeholder="Search"
                prefix={<SearchOutlined style={{ color: "#AAA" }} />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{
                  width: "292px",
                  height: "36px",
                  borderRadius: "6px",
                  border: "1px solid #CCC",
                  backgroundColor: "#FFF",
                  fontFamily: "Open Sans",
                  fontSize: "16px",
                  color: "#AAA",
                }}
              />
            </Col>
            <Col>
              <Space align="center" size={20}>
                <Text
                  style={{
                    color: "#4D4D4D",
                    fontFamily: "Open Sans",
                    fontSize: "16px",
                    fontWeight: "700",
                    lineHeight: "24px",
                    letterSpacing: "0.5px",
                  }}
                >
                  {activeUsersCount} Active Users
                </Text>
                <Button
                  type="primary"
                  style={{
                    backgroundColor: "#DF701F",
                    borderColor: "#DF701F",
                    borderRadius: "6px",
                    height: "36px",
                    padding: "0 10px",
                    fontFamily: "Open Sans",
                    fontSize: "16px",
                    fontWeight: "400",
                    lineHeight: "16px",
                  }}
                >
                  + Add User
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[0, 0]}>
        <Col span={24}>
          <div
            style={{
              width: "100%",
              borderRadius: "10px",
              boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.20)",
              backgroundColor: "#FFF",
              overflow: "hidden",
            }}
          >
            <Table
              columns={columns}
              dataSource={filteredData}
              pagination={false}
              scroll={{ x: 1536 }}
              style={{
                width: "100%",
              }}
              components={{
                header: {
                  cell: (props: any) => (
                    <th
                      {...props}
                      style={{
                        ...props.style,
                        backgroundColor: "#F5F5F5",
                        color: "#4D4D4D",
                        fontFamily: "Open Sans",
                        fontSize: "16px",
                        fontWeight: "600",
                        lineHeight: "20px",
                        padding: "15px 20px",
                        borderBottom: "none",
                      }}
                    />
                  ),
                },
                body: {
                  cell: (props: any) => (
                    <td
                      {...props}
                      style={{
                        ...props.style,
                        backgroundColor: "#FFF",
                        padding: "15px 20px",
                        borderTop: "1px solid #EEE",
                        borderBottom: "1px solid #EEE",
                      }}
                    />
                  ),
                },
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Users;
